import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { apiCall } from "../Apis/apiCall";
import CustomSelect from "./ui/CustomSelect";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import Shimmer from "./ui/Shimmer";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const VehicleModelsLayer = () => {
  const [vehicleModels, setVehicleModels] = useState([]);
  const [filteredVehicleModels, setFilteredVehicleModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState(""); // 'add', 'edit', or 'delete'
  const [showModal, setShowModal] = useState(false);
  const [vehicleModelId, setVehicleModelId] = useState(null);
  const [modelName, setModelName] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [modelImage, setModelImage] = useState(null);
  const [selectedVehicleModel, setSelectedVehicleModel] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [vehicleModelLoading, setVehicleModelLoading] = useState(false);
  const [makeId, setMakeId] = useState(0);
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [modalMakeId, setModalMakeId] = useState(null); // Separate makeId for modal
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: "model_name",
    direction: "asc",
  }); // Added missing sortConfig state
  const [expandedRows, setExpandedRows] = useState({}); // Added missing expandedRows state
  const [showImageModal, setShowImageModal] = useState(false); // Added missing showImageModal state
  const [selectedImage, setSelectedImage] = useState(null); // Added missing selectedImage state
  const cropperRef = useRef(null);
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch all vehicle makes with search functionality
  const fetchVehicleMakes = async () => {
    setError(null);
    try {
      const response = await apiCall("GET", "/get-all-vehicle-makes", {
        per_page: 100,
        sort_by: "make_name",
        sort_order: "asc",
        want_pagination: 0,
      });

      if (response?.data?.data?.data) {
        setVehicleMakes(response.data.data.data);
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setError(true);
    }
  };

  const fetchVehicleModels = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = "model_name",
    sortOrder = "asc"
  ) => {
    setLoading(true);
    try {
      const params = {
        sort_order: sortOrder,
        per_page: perPage,
        page: page,
        want_pagination: 1,
      };

      if (search.trim()) {
        params.search = search;
      }

      const response = await apiCall(
        "GET",
        `/get-vehicle-models-by-make/${makeId || 0}`,
        params
      );

      if (response?.data?.data) {
        const apiData = response.data.data;
        const modelsData = apiData.data || [];

        const mappedData = modelsData.map((model) => ({
          type_id: model.model_id,
          type_name: model.model_name,
          type_image: model.model_image,
          created_datetime: model.created_datetime,
          is_active: model.is_active,
          description: "",
          make_name: model.make_name,
          make_id: model.make_id,
        }));

        setVehicleModels(mappedData);
        setFilteredVehicleModels(mappedData);
        setTotalEntries(apiData.total || 0);
        setTotalPages(
          Math.ceil((apiData.total || 0) / (apiData.per_page || 10))
        );
        setCurrentPage(apiData.current_page || 1);
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setVehicleModels([]);
      setFilteredVehicleModels([]);
      setTotalEntries(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleModelById = async (modelId) => {
    setVehicleModelLoading(true);
    try {
      const response = await apiCall("GET", `/get-vehicle-model/${modelId}`);
      if (response?.data?.data) {
        const model = response.data.data;
        return {
          type_id: model.model_id,
          type_name: model.model_name,
          type_image: model.model_image,
          created_datetime: model.created_datetime,
          is_active: model.is_active,
          description: "",
          make_name: model.make_name,
          make_id: model.make_id,
        };
      }
      return null;
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      return null;
    } finally {
      setVehicleModelLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchVehicleModels = useCallback(
    debounce((page, perPage, search) => {
      fetchVehicleModels(
        page,
        perPage,
        search,
        sortConfig.field,
        sortConfig.direction
      );
    }, 500),
    [sortConfig, makeId]
  );

  const debouncedFetchVehicleMakes = useCallback(
    debounce((search, isForModal = false) => {
      fetchVehicleMakes(search, isForModal);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedFetchVehicleModels(1, perPage, value);
  };

  const handleSort = (field) => {
    if (field === "created_by") return;
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedVehicleModels = [...filteredVehicleModels];

  useEffect(() => {
    fetchVehicleMakes();
  }, []);

  // Handle query parameters to auto-open the add modal
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const makeIdFromQuery = queryParams.get("makeId");
    const addModel = queryParams.get("addModel");

    if (makeIdFromQuery && addModel === "true") {
      setMakeId(parseInt(makeIdFromQuery, 10));
      setModalMakeId(parseInt(makeIdFromQuery, 10)); // Set modalMakeId as well
      handleOpenModal("add");
      // Clean up the URL after opening the modal
      navigate("/vehicle-models", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    fetchVehicleModels(
      currentPage,
      perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [currentPage, perPage, sortConfig.field, sortConfig.direction, makeId]);

  const handleOpenModal = async (type, vehicleModel = null) => {
    setModalType(type);
    setShowModal(true);
    setVehicleModelId(null);
    setModelName("");
    setModelImage(null);
    setUploadedImageUrl(null);
    setImageUploading(false);
    setSelectedVehicleModel(null);
    setModalMakeId(makeId || null); // Pre-select the makeId from the parent component

    if (type === "edit" && vehicleModel) {
      if (!vehicleModel.type_id) {
        handleCustomResponse("error", "Invalid or missing Vehicle Model ID.");
        return;
      }
      const vehicleModelData = await fetchVehicleModelById(
        vehicleModel.type_id
      );
      if (vehicleModelData) {
        setVehicleModelId(vehicleModelData.type_id);
        setModelName(vehicleModelData.type_name || "");
        setUploadedImageUrl(vehicleModelData.type_image || null);
        setSelectedVehicleModel(vehicleModelData);
        setModalMakeId(vehicleModelData.make_id);
      } else {
        handleCustomResponse("error", "Failed to load vehicle model details.");
      }
    } else if (type === "delete" && vehicleModel) {
      if (!vehicleModel.type_id) {
        handleCustomResponse("error", "Invalid or missing Vehicle Model ID.");
        return;
      }
      setSelectedVehicleModel(vehicleModel);
      setVehicleModelId(vehicleModel.type_id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setVehicleModelId(null);
    setModelName("");
    setModelImage(null);
    setUploadedImageUrl(null);
    setImageUploading(false);
    setSelectedVehicleModel(null);
    setModalMakeId(null);
  };

  const handleOpenImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        handleCustomResponse("error", "Image size should not exceed 5MB.");
        return;
      }
      if (!file.type.startsWith("image/")) {
        handleCustomResponse("error", "Please select a valid image file.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropModal(true);
        setAspectRatio("1:1");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = async () => {
    if (cropperRef.current) {
      const canvas = cropperRef.current.cropper.getCroppedCanvas({
        width: 200,
        height: 200,
      });

      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            handleCustomResponse("error", "Failed to crop image.");
            return;
          }
          const file = new File([blob], `vehicle-model-${Date.now()}.png`, {
            type: "image/png",
          });
          setModelImage(file);
          setShowCropModal(false);
          await handleImageUpload(file);
        },
        "image/png",
        0.8
      );
    } else {
      handleCustomResponse("error", "Cropper not initialized.");
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      handleCustomResponse("error", "No image selected for upload.");
      return;
    }

    try {
      setImageUploading(true);
      const formData = new FormData();
      formData.append("vehicle_model", file);

      const response = await apiCall(
        "POST",
        "/upload-image",
        formData,
        null,
        true
      );

      if (response?.data?.original) {
        setUploadedImageUrl(response.data.original);
        setModelImage(file);
        handleCustomResponse("success", "Image uploaded successfully!");
      } else {
        handleCustomResponse("error", "Failed to upload image.");
        setModelImage(null);
      }
    } catch (error) {
      handleCustomResponse("error", "Error uploading image.");
      setModelImage(null);
    } finally {
      setImageUploading(false);
    }
  };

  const handleCloseCropModal = () => {
    setShowCropModal(false);
    setImageToCrop(null);
    setAspectRatio("1:1");
  };

  const handleCropMove = () => {
    if (cropperRef.current) {
      const cropBoxData = cropperRef.current.cropper.getCropBoxData();
      const width = cropBoxData.width;
      const height = cropBoxData.height;
      const ratio = width / height;
      setAspectRatio(`${(width / height).toFixed(2)}:1`);
    }
  };

  const handleAddVehicleModel = async () => {
    if (!modelName.trim()) {
      handleCustomResponse("error", "Vehicle Model Name is required.");
      return;
    }

    if (!modalMakeId) {
      handleCustomResponse("error", "Please select a vehicle make.");
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        make_id: modalMakeId,
        model_name: modelName,
        model_image: uploadedImageUrl || "",
      };

      const response = await apiCall("POST", "/add-vehicle-model", payload);

      if (response?.data) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "POST");
        setModelName("");
        setModelImage(null);
        setUploadedImageUrl(null);
        await fetchVehicleModels(currentPage, perPage, searchQuery);
        handleCloseModal();
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateVehicleModel = async () => {
    if (!modelName.trim()) {
      handleCustomResponse("error", "Vehicle Model Name is required.");
      return;
    }

    if (!vehicleModelId) {
      handleCustomResponse("error", "Invalid or missing Vehicle Model ID.");
      return;
    }

    if (!modalMakeId) {
      handleCustomResponse("error", "Please select a vehicle make.");
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        make_id: modalMakeId,
        model_name: modelName,
        model_image: uploadedImageUrl || selectedVehicleModel?.type_image || "",
      };

      const response = await apiCall(
        "PUT",
        `/update-vehicle-model/${vehicleModelId}`,
        payload
      );

      if (response?.data) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "PUT");
        setModelName("");
        setModelImage(null);
        setUploadedImageUrl(null);
        setVehicleModelId(null);
        await fetchVehicleModels(currentPage, perPage, searchQuery);
        handleCloseModal();
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteVehicleModel = async () => {
    if (!vehicleModelId) {
      handleCustomResponse("error", "Invalid or missing Vehicle Model ID.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "DELETE",
        `/delete-vehicle-model/${vehicleModelId}`
      );

      const handleRes = response.data;
      handleApiResponse(handleRes, "success", "DELETE");
      await fetchVehicleModels(currentPage, perPage, searchQuery);
      handleCloseModal();
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "add") {
      handleAddVehicleModel();
    } else if (modalType === "edit") {
      handleUpdateVehicleModel();
    } else if (modalType === "delete") {
      handleDeleteVehicleModel();
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const toggleExpand = (typeId, field) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${typeId}_${field}`]: !prev[`${typeId}_${field}`],
    }));
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);

  if (error) {
    return <ErrorAlert />;
  }
  const MakesDropdown = ({ selectedMakeId, onSelect, disabled = false }) => {
    const [localMakes, setLocalMakes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef(null);

    const fetchMakes = async (search = "") => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiCall("GET", "/get-all-vehicle-makes", {
          search,
          per_page: 100,
          sort_by: "make_name",
          sort_order: "asc",
        });
        if (response?.data?.data?.data) {
          setLocalMakes(response.data.data.data);
          const handleRes = response.data;
          handleApiResponse(handleRes, "success", "GET");
        }
      } catch (error) {
        const handleErr = error.response?.data;
        handleApiResponse(handleErr, "Err", "GET");
        setError(true);
        setLocalMakes([]);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (showDropdown) {
        fetchMakes(searchQuery);
      }
    }, [searchQuery, showDropdown]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setShowDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (error) {
      return <ErrorAlert />;
    }
    return (
      <div className="position-relative" ref={dropdownRef}>
        <button
          className="btn btn-sm btn-outline-secondary dropdown-toggle w-100 text-start d-flex justify-content-between align-items-center"
          type="button"
          disabled={disabled}
          onClick={() => setShowDropdown(!showDropdown)}
          style={{ minHeight: "31px" }}
        >
          <span>
            {selectedMakeId === 0
              ? "All Makes"
              : vehicleMakes.find((make) => make.make_id === selectedMakeId)
                  ?.make_name || "Select Make"}
          </span>
          <i
            className={`fas fa-chevron-${showDropdown ? "up" : "down"}`}
            style={{ fontSize: "12px" }}
          ></i>
        </button>

        {showDropdown && (
          <div
            className="border bg-white shadow-lg rounded"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1050,
              marginTop: "2px",
              maxHeight: "250px",
            }}
          >
            <div className="p-2 border-bottom">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search makes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{ maxHeight: "180px", overflowY: "auto" }}>
              {loading ? (
                <div className="text-center p-3">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></div>
                </div>
              ) : (
                <>
                  <button
                    className={`w-100 text-start border-0 px-3 py-2 ${
                      selectedMakeId === 0
                        ? "bg-primary text-white"
                        : "bg-white"
                    }`}
                    onClick={() => {
                      onSelect(0);
                      setShowDropdown(false);
                      setSearchQuery("");
                    }}
                  >
                    All Makes
                  </button>
                  {localMakes.length === 0 ? (
                    <div className="px-3 py-2 text-muted">No makes found</div>
                  ) : (
                    localMakes.map((make) => (
                      <button
                        key={make.make_id}
                        className={`w-100 text-start border-0 px-3 py-2 ${
                          make.make_id === selectedMakeId
                            ? "bg-primary text-white"
                            : "bg-white"
                        }`}
                        onClick={() => {
                          onSelect(make.make_id);
                          setShowDropdown(false);
                          setSearchQuery("");
                        }}
                      >
                        {make.make_name}
                      </button>
                    ))
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <style>
        {`
          .table-responsive .table td, .table-responsive .table th {
            max-width: 200px;
            vertical-align: middle;
          }
          .show-more-btn {
            color: #E63C23;
            cursor: pointer;
            font-size: 12px;
            margin-left: 5px;
            background: none;
            border: none;
            padding: 0;
          }
          .show-more-btn:hover {
            text-decoration: underline;
          }
          .image-preview {
            cursor: pointer;
            transition: transform 0.2s;
          }
          .image-preview:hover {
            transform: scale(1.05);
          }
          @media (max-width: 576px) {
            .table-responsive .table td, .table-responsive .table th {
              max-width: 100px;
            }
            .show-more-btn {
              font-size: 10px;
            }
          }
          .cropper-container {
            max-height: 400px;
            max-width: 100%;
          }
          .aspect-ratio-display {
            font-size: 14px;
            color: #333;
            margin-top: 10px;
            text-align: center;
          }
        `}
      </style>
      <div className="p-4 p-md-3 p-sm-2">
        <div className="card shadow-sm">
          <div className="card p-3 p-sm-2 p-md-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div
                className="icon-field flex-grow-1"
                style={{ maxWidth: "400px" }}
              >
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by vehicle model..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <span className="icon">
                  <Icon icon="ion:search-outline" />
                </span>
              </div>
              <PerPageDropdown
                perPage={perPage}
                handlePerPageChange={(value) => {
                  setPerPage(value);
                  setCurrentPage(1);
                }}
              />
              <div className="flex-grow-1" style={{ maxWidth: "200px" }}>
                <CustomSelect
                  name="vehicleMakes"
                  value={makeId}
                  onChange={(selected) => setMakeId(selected?.value || 0)}
                  options={[
                    { make_id: 0, make_name: "All Makes" },
                    ...vehicleMakes,
                  ]}
                  placeholder="Select Make"
                  labelKey="make_name"
                  valueKey="make_id"
                  isSearchable={true}
                  isClearable={false}
                  isLoading={makesLoading}
                />
              </div>
              <div className="ms-auto">
                {hasPermission("insert_vehicle_models") && (
                  <button
                    className="btn btn-primary-600 btn-sm d-flex align-items-center"
                    onClick={() => handleOpenModal("add")}
                    disabled={makeId === 0}
                  >
                    <Icon icon="ri:add-line" className="me-1" /> Add Vehicle
                    Model
                  </button>
                )}
              </div>
            </div>

            {sortedVehicleModels.length === 0 && !loading ? (
              <TableDataNoAvailable
                message={
                  makeId
                    ? "No vehicle models found."
                    : "Please select a vehicle make."
                }
              />
            ) : (
              <div className="position-relative">
                <div
                  className="table-responsive"
                  style={{
                    opacity: loading ? 0.4 : 1,
                    pointerEvents: loading ? "none" : "auto",
                  }}
                >
                  <table className="table bordered-table mb-0 table-hover">
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col" className="p-2">
                          <span>S.No</span>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("type_name")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Vehicle Model</span>
                            <SortIcons
                              field="type_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_datetime")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="created_datetime"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("type_image")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Image</span>
                            <SortIcons
                              field="type_image"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("is_active")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex">
                            <span>Status</span>
                            <SortIcons
                              field="is_active"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedVehicleModels.map((vehicleModel, index) => (
                        <tr key={vehicleModel.type_id || index}>
                          <td className="p-2">
                            {(currentPage - 1) * perPage + index + 1}
                          </td>
                          <td className="p-2">
                            <span className="">
                              {expandedRows[`${vehicleModel.type_id}_type_name`]
                                ? vehicleModel.type_name || "-"
                                : truncateText(vehicleModel.type_name || "-")}
                              {vehicleModel.type_name &&
                                vehicleModel.type_name.length > 50 && (
                                  <button
                                    className="show-more-btn"
                                    onClick={() =>
                                      toggleExpand(
                                        vehicleModel.type_id,
                                        "type_name"
                                      )
                                    }
                                  >
                                    {expandedRows[
                                      `${vehicleModel.type_id}_type_name`
                                    ]
                                      ? "Show Less"
                                      : "Show More"}
                                  </button>
                                )}
                            </span>
                          </td>
                          <td className="p-2">
                            {new Date(
                              vehicleModel.created_datetime
                            ).toLocaleString() || "-"}
                          </td>
                          <td className="p-2">
                            {vehicleModel.type_image ? (
                              <img
                                src={vehicleModel.type_image}
                                alt={vehicleModel.type_name}
                                className="image-preview"
                                style={{ maxWidth: "50px", maxHeight: "50px" }}
                                onClick={() =>
                                  handleOpenImageModal(vehicleModel.type_image)
                                }
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="p-2">
                            <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                vehicleModel.is_active
                                  ? "bg-success-focus text-success-600 border border-success-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"
                              }`}
                            >
                              {vehicleModel.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="p-2">
                            {hasPermission("update_vehicle_models") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Edit";
                                  tooltip.style.cssText = `
                                    position: absolute;
                                    top: -35px;
                                    left: 50%;
                                    transform: translateX(-50%);
                                    background-color: #333;
                                    color: white;
                                    padding: 5px 8px;
                                    border-radius: 4px;
                                    font-size: 12px;
                                    white-space: nowrap;
                                    z-index: 1000;
                                    pointer-events: none;
                                  `;
                                  tooltip.id = "edit-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("edit-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() =>
                                  handleOpenModal("edit", vehicleModel)
                                }
                              >
                                <Icon icon="lucide:edit" />
                              </button>
                            )}
                            {hasPermission("delete_vehicle_models") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Delete";
                                  tooltip.style.cssText = `
                                    position: absolute;
                                    top: -35px;
                                    left: 50%;
                                    transform: translateX(-50%);
                                    background-color: #333;
                                    color: white;
                                    padding: 5px 8px;
                                    border-radius: 4px;
                                    font-size: 12px;
                                    white-space: nowrap;
                                    z-index: 1000;
                                    pointer-events: none;
                                  `;
                                  tooltip.id = "delete-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("delete-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() =>
                                  handleOpenModal("delete", vehicleModel)
                                }
                              >
                                <Icon icon="mingcute:delete-2-line" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {loading && (
                  <div
                    className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                    style={{
                      zIndex: 10,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <LoadingComponent />
                  </div>
                )}
                <PaginationControls
                  pagination={{
                    currentPage,
                    perPage,
                    total: totalEntries,
                  }}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Modal */}
      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "add"
                  ? "Add New Vehicle Model"
                  : modalType === "edit"
                  ? "Edit Vehicle Model"
                  : "Delete Vehicle Model"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {selectedVehicleModel?.type_name || "this vehicle model"}
                  </strong>
                  ?
                </p>
              ) : (
                <>
                  {vehicleModelLoading ? (
                    <Shimmer type="modal" />
                  ) : (
                    <>
                      <div className="mb-3">
                        <label htmlFor="makeDropdown" className="form-label">
                          Vehicle Make
                        </label>
                        <CustomSelect
                          name="modalVehicleMakes"
                          value={modalMakeId}
                          onChange={(selected) =>
                            setModalMakeId(selected?.value)
                          }
                          options={vehicleMakes}
                          placeholder="Select Make"
                          labelKey="make_name"
                          valueKey="make_id"
                          isSearchable={true}
                          isClearable={false}
                          isLoading={makesLoading}
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="modelName" className="form-label">
                          Vehicle Model Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="modelName"
                          value={modelName}
                          onChange={(e) => setModelName(e.target.value)}
                          placeholder="Enter vehicle model name"
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="modelImage" className="form-label">
                          Vehicle Model Image
                        </label>

                        {/* Hidden actual file input */}
                        <input
                          type="file"
                          className="form-control d-none"
                          id="modelImage"
                          accept="image/*"
                          onChange={handleImageSelect}
                          disabled={imageUploading}
                          ref={(input) => {
                            window.modelFileInput = input;
                          }}
                        />

                        {/* Custom styled "file input" */}
                        <div
                          className="form-control d-flex align-items-center justify-content-between"
                          style={{
                            cursor: imageUploading ? "not-allowed" : "pointer",
                            opacity: imageUploading ? 0.6 : 1,
                          }}
                          onClick={() =>
                            !imageUploading && window.modelFileInput?.click()
                          }
                        >
                          <span
                            className={`${
                              uploadedImageUrl ||
                              selectedVehicleModel?.type_image
                                ? "text-primary"
                                : "text-muted"
                            }`}
                          >
                            {modelImage
                              ? modelImage.name
                              : selectedVehicleModel?.type_image
                              ? selectedVehicleModel.type_image.split("/").pop()
                              : "No file chosen"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            style={{ pointerEvents: "none" }}
                          >
                            Choose File
                          </button>
                        </div>

                        {imageUploading && (
                          <div className="mt-2">
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Uploading...
                              </span>
                            </div>
                            <span className="ms-2">Uploading image...</span>
                          </div>
                        )}

                        {modelImage && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(modelImage)}
                              alt="Preview"
                              className="image-preview"
                              style={{ maxWidth: "100px", maxHeight: "100px" }}
                              onClick={() =>
                                handleOpenImageModal(
                                  URL.createObjectURL(modelImage)
                                )
                              }
                            />
                          </div>
                        )}

                        {selectedVehicleModel?.type_image && !modelImage && (
                          <div className="mt-2">
                            <img
                              src={selectedVehicleModel.type_image}
                              alt={selectedVehicleModel.type_name}
                              className="image-preview"
                              style={{ maxWidth: "100px", maxHeight: "100px" }}
                              onClick={() =>
                                handleOpenImageModal(
                                  selectedVehicleModel.type_image
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  modalType === "delete" ? "btn-danger" : "btn-primary-600"
                }`}
                onClick={handleSubmit}
                disabled={
                  saveLoading || (modalType !== "delete" && !modalMakeId)
                }
              >
                {modalType === "add"
                  ? saveLoading
                    ? "Please wait..."
                    : "Add Vehicle Model"
                  : modalType === "edit"
                  ? saveLoading
                    ? "Please wait..."
                    : "Update Vehicle Model"
                  : saveLoading
                  ? "Please wait..."
                  : "Delete Vehicle Model"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      <div
        className={`modal fade ${showCropModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showCropModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Crop Image</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseCropModal}
              ></button>
            </div>
            <div className="modal-body">
              {imageToCrop && (
                <>
                  <Cropper
                    src={imageToCrop}
                    style={{ height: 400, width: "100%" }}
                    initialAspectRatio={1}
                    aspectRatio={1}
                    guides={true}
                    viewMode={1}
                    minCropBoxWidth={200}
                    minCropBoxHeight={200}
                    background={false}
                    responsive={true}
                    autoCropArea={1}
                    checkOrientation={false}
                    crop={handleCropMove}
                    ref={cropperRef}
                  />
                  <p className="aspect-ratio-display">
                    Current Aspect Ratio: {aspectRatio}
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseCropModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary-600"
                onClick={handleCrop}
              >
                Crop & Upload
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      <div
        className={`modal fade ${showImageModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showImageModal ? "rgba(0,0,0,0.8)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Image Preview</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseImageModal}
              ></button>
            </div>
            <div className="modal-body d-flex justify-content-center align-items-center">
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Vehicle Model"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleModelsLayer;
